import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import BizVerificationComplete from 'dashboard/components/PlanUpgradeFlow/BizVerificationComplete';
import BizVerificationInquiry from 'dashboard/components/PlanUpgradeFlow/BizVerificationInquiry';
import ComparePlans from 'dashboard/components/PlanUpgradeFlow/ComparePlans';
import PaymentMethod from 'dashboard/components/PlanUpgradeFlow/PaymentMethod';
import PlanUpgradeInstructions from 'dashboard/components/PlanUpgradeFlow/PlanUpgradeInstructions';
import UnsupportedSolutionsWarning from 'dashboard/components/PlanUpgradeFlow/UnsupportedSolutionsWarning';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { Blueprint } from 'dashboard/models/blueprint';
import { Organization, ProductFeatureName } from 'dashboard/models/organization';
import {
  AvailableOrganizationPlan,
  OrganizationPlan,
  OrganizationPlanKey,
} from 'dashboard/models/organization-plan';
import { OrganizationPlanMigration } from 'dashboard/models/organization-plan-migration';
import { User } from 'dashboard/models/user';
import { RootState } from 'dashboard/redux/store';
import { Paragraph } from 'design-system/components/Text';
import { SegmentClient } from 'lib/clients/SegmentClient';
import { captureMessage } from 'lib/reporting';

// This component can either be used to show a flow to upgrade to a preselected plan, or
// to upgrade to a dynamically selected plan based on a set of required product features.

type PlanUpgradeFlowPreselectedPlanProps = {
  requestedOrganizationPlan: AvailableOrganizationPlan;
  blueprints: Blueprint[];
  loadBlueprints: () => Promise<void>;
};

type PlanUpgradeFlowDynamicPlanProps = {
  requiredProductFeatures: ProductFeatureName[];
  loadingAvailablePlans: boolean;
  handleLoadAvailablePlans: (
    requiredProductFeatures: ProductFeatureName[],
  ) => Promise<AvailableOrganizationPlan[]>;
};

type PlanUpgradeFlowSharedProps = {
  // Note: Please coordinate with the Growth team on the `requestedFeatureName`
  // that will be passed into the Biz Verification Inquiry.
  requestedFeatureName: string;
  handleSubmitBillingInfo: (stripeId: string) => Promise<void>;
  handleCreateOrganizationPlanMigration: (
    inquiryId: string,
    selectedPlanKey: string,
  ) => Promise<OrganizationPlanMigration>;
  showMessage: (message: string) => void;
  onNavBack: () => void;
  onClose: () => void;
};

type PlanUpgradeFlowProps = PlanUpgradeFlowSharedProps &
  (PlanUpgradeFlowPreselectedPlanProps | PlanUpgradeFlowDynamicPlanProps);

export enum View {
  ComparePlans = 'compare-plans',
  PlanUpgradeInstructions = 'plan-upgrade-instructions',
  UnsupportedSolutionsWarning = 'unsupported-solutions-warning',
  PaymentMethod = 'payment-method',
  BizVerificationInquiry = 'biz-verification-inquiry',
  BizVerificationComplete = 'biz-verification-complete',
}

// These are the only OrganizationPlans that can currently be upgraded to by providing
// payment information and biz verification information in the Dashboard.
export const SUPPORTED_PLANS_FOR_SELF_SERVE_UPGRADE = [
  OrganizationPlanKey.Starter,
  OrganizationPlanKey.StarterPenguin,
  OrganizationPlanKey.Essential,
];

const PlanUpgradeFlow = (props: PlanUpgradeFlowProps) => {
  const { currentUser, organization, currentOrganizationPlan } = useSelector<
    RootState,
    { currentUser: User; organization: Organization; currentOrganizationPlan: OrganizationPlan }
  >((state) => {
    return {
      currentUser: state.currentUser.currentUser,
      organization: state.organization.organization,
      currentOrganizationPlan: state.organization.currentOrganizationPlan,
    };
  });

  const { 'billing-info': billingInfo } = organization.attributes;

  const flowStartsAtComparePlans = 'requiredProductFeatures' in props;

  const [currentView, setView] = useState<View>(
    flowStartsAtComparePlans ? View.ComparePlans : View.PlanUpgradeInstructions,
  );
  const [newOrganizationPlan, setNewOrganizationPlan] = useState<AvailableOrganizationPlan | null>(
    'requestedOrganizationPlan' in props ? props.requestedOrganizationPlan : null,
  );

  useEffect(() => {
    SegmentClient.sendSegmentTrackEvent('Organization Plan Upgrade - Flow started', {
      requestedFeatureName: props.requestedFeatureName,
      newOrganizationPlan: newOrganizationPlan?.key,
    });
  }, []);

  const handleNavToNextView = (
    currentView: View,
    newOrganizationPlan: AvailableOrganizationPlan | null,
  ): void => {
    let nextView = null;

    if (currentView === View.ComparePlans) {
      if (
        newOrganizationPlan &&
        SUPPORTED_PLANS_FOR_SELF_SERVE_UPGRADE.includes(newOrganizationPlan.key)
      ) {
        nextView = View.PlanUpgradeInstructions;
      } else {
        // Note: In this case, we proceed straight to the Biz Verification Inquiry instead of
        // the plan upgrade instructions, because the Inquiry Template is set up to only collect
        // basic information for plans outside of SUPPORTED_PLANS_FOR_SELF_SERVE_UPGRADE.
        nextView = View.BizVerificationInquiry;
      }
    } else if (currentView === View.PlanUpgradeInstructions) {
      if ('blueprints' in props && newOrganizationPlan?.['archives-blueprints']) {
        nextView = View.UnsupportedSolutionsWarning;
      } else {
        nextView = View.PaymentMethod;
      }
    } else if (currentView === View.UnsupportedSolutionsWarning) {
      nextView = View.PaymentMethod;
    } else if (currentView === View.PaymentMethod) {
      nextView = View.BizVerificationInquiry;
    } else if (currentView === View.BizVerificationInquiry) {
      nextView = View.BizVerificationComplete;
    }

    if (nextView) {
      setView(nextView);
      SegmentClient.sendSegmentTrackEvent('Organization Plan Upgrade - Navigated to new view', {
        newOrganizationPlan: newOrganizationPlan?.key,
        requestedFeatureName: props.requestedFeatureName,
        currentView,
        nextView,
      });
    } else {
      captureMessage('PlanUpgradeFlow cannot compute the next view to transition to', 'warning', {
        requestedFeatureName: props.requestedFeatureName,
        newOrganizationPlan: newOrganizationPlan?.key,
        currentView,
      });
    }
  };

  const handleNavToPreviousView = (currentView: View): void => {
    if (currentView === View.ComparePlans) {
      props.onNavBack();
    } else if (currentView === View.PlanUpgradeInstructions) {
      if (flowStartsAtComparePlans) {
        setView(View.ComparePlans);
      } else {
        props.onNavBack();
      }
    } else if (currentView === View.UnsupportedSolutionsWarning) {
      setView(View.PlanUpgradeInstructions);
    } else if (currentView === View.PaymentMethod) {
      setView(View.PlanUpgradeInstructions);
    } else if ((currentView = View.BizVerificationInquiry)) {
      if (flowStartsAtComparePlans) {
        setView(View.ComparePlans);
      } else {
        props.onNavBack();
      }
    }
  };

  // This skips the payment method screen if billing info is already present, and it also
  // triggers a navigation once the user has submitted their billing info.
  useEffect(() => {
    if (currentView === View.PaymentMethod && billingInfo != null) {
      handleNavToNextView(View.PaymentMethod, newOrganizationPlan);
    }
  }, [currentView, billingInfo?.['card-last-4']]);

  if (currentView === View.ComparePlans) {
    if (flowStartsAtComparePlans) {
      return (
        <ComparePlans
          currentOrganizationPlan={currentOrganizationPlan}
          newOrganizationPlan={newOrganizationPlan}
          requiredProductFeatures={props.requiredProductFeatures}
          loadingAvailablePlans={props.loadingAvailablePlans}
          handleLoadAvailablePlans={props.handleLoadAvailablePlans}
          setNewOrganizationPlan={setNewOrganizationPlan}
          handleNavToNextView={handleNavToNextView}
          handleNavToPreviousView={handleNavToPreviousView}
          onClose={props.onClose}
        />
      );
    } else {
      // Note: This path should never be hit, because the ComparePlans view should only be shown
      // when requiredProductFeatures is present in props.
      captureMessage('PlanUpgradeFlow is on ComparePlans screen with unexpected props', 'warning', {
        requestedFeatureName: props.requestedFeatureName,
        newOrganizationPlan: newOrganizationPlan?.key,
      });
      return <ErrorScreen onClose={props.onClose} />;
    }
  }

  if (currentView === View.PlanUpgradeInstructions) {
    return (
      <PlanUpgradeInstructions
        newOrganizationPlan={newOrganizationPlan}
        handleNavToNextView={handleNavToNextView}
        handleNavToPreviousView={handleNavToPreviousView}
        onClose={props.onClose}
      />
    );
  }

  if (currentView === View.UnsupportedSolutionsWarning) {
    if ('blueprints' in props) {
      return (
        <UnsupportedSolutionsWarning
          newOrganizationPlan={newOrganizationPlan}
          blueprints={props.blueprints}
          loadBlueprints={props.loadBlueprints}
          handleNavToNextView={handleNavToNextView}
          handleNavToPreviousView={handleNavToPreviousView}
          onClose={props.onClose}
        />
      );
    } else {
      // Note: This path should never be hit, because in `handleNavToNextView`, this view is only transitioned to
      // if `blueprints` is present in the props.
      captureMessage(
        'PlanUpgradeFlow is on UnsupportedSolutionsWarning screen with unexpected props',
        'warning',
        {
          requestedFeatureName: props.requestedFeatureName,
          newOrganizationPlan: newOrganizationPlan?.key,
        },
      );
      return <ErrorScreen onClose={props.onClose} />;
    }
  }

  if (currentView === View.PaymentMethod) {
    return (
      <PaymentMethod
        handleSubmitBillingInfo={props.handleSubmitBillingInfo}
        showMessage={props.showMessage}
        handleNavToPreviousView={handleNavToPreviousView}
        onClose={props.onClose}
      />
    );
  }

  if (currentView === View.BizVerificationInquiry) {
    if (newOrganizationPlan) {
      return (
        <BizVerificationInquiry
          newOrganizationPlan={newOrganizationPlan}
          organization={organization}
          currentUser={currentUser}
          requestedFeatureName={props.requestedFeatureName}
          handleCreateOrganizationPlanMigration={props.handleCreateOrganizationPlanMigration}
          handleNavToNextView={handleNavToNextView}
          handleNavToPreviousView={handleNavToPreviousView}
          onClose={props.onClose}
        />
      );
    } else {
      // Note: This path should never be hit, because the BizVerificationInquiry view should only be shown
      // after the newOrganizationPlan has been set either via props or by the ComparePlans view.
      captureMessage(
        'PlanUpgradeFlow is on BizVerificationInquiry screen with no newOrganizationPlan',
        'warning',
        {
          requestedFeatureName: props.requestedFeatureName,
        },
      );
      return <ErrorScreen onClose={props.onClose} />;
    }
  }

  if (currentView === View.BizVerificationComplete) {
    return <BizVerificationComplete onClose={props.onClose} />;
  }

  return <ErrorScreen onClose={props.onClose} />;
};

const ErrorScreen = ({ onClose }: { onClose: () => void }) => {
  return (
    <StyledModalContainer>
      <ModalHeader onClose={onClose}>Oops!</ModalHeader>
      <ModalBody>
        <Paragraph size='lg' color='primary'>
          Sorry, something went wrong. Please try again.
        </Paragraph>
      </ModalBody>
    </StyledModalContainer>
  );
};

const StyledModalContainer = styled(ModalContainer)`
  min-width: 700px;
`;

export default PlanUpgradeFlow;
